import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useState } from 'react';

/**
 * LogContext: A global context for managing log event data
 * 
 * This context is responsible for:
 * 1. Storing all incoming log event data in a persistent cache
 * 2. Processing log events in the background regardless of which component is visible
 * 3. Organizing logs in a hierarchical structure: log_path → files → events
 * 4. Handling log retention and archiving
 * 5. Calculating severity/health indicators at file and path levels
 * 6. Providing access to the latest logs for all components
 * 7. Notifying components when new data is available via custom events
 * 8. Optimizing performance with module-level state management
 * 9. Ensuring real-time updates across the application
 */

// Create context
const LogContext = createContext();

// Module-level state to ensure continuous processing regardless of React component lifecycle
let moduleState = {
  logData: {},
  lastUpdate: Date.now()
};

// BroadcastChannel for cross-tab synchronization
let broadcastChannel;
try {
  broadcastChannel = new BroadcastChannel('log-monitor-updates');
} catch (e) {
  console.warn('BroadcastChannel not supported in this browser, cross-tab sync disabled');
}

// Retention timeframes (in milliseconds)
const RETENTION = {
  CRITICAL_ERROR: 24 * 60 * 60 * 1000, // 24 hours (Critical/Error logs)
  OTHER: 1 * 60 * 60 * 1000            // 1 hour (Info/Warning logs)
};

// Severity levels with their priorities (higher = more severe)
const SEVERITY_LEVELS = {
  Debug: 0,
  Info: 1,
  Warning: 2,
  Error: 3,
  Critical: 4,
  SegmentFault: 5
};

// Logger for LogContext
const LLogger = {
  log: (...args) => console.log('[LogContext]', ...args),
  warn: (...args) => console.warn('[LogContext]', ...args),
  error: (...args) => console.error('[LogContext]', ...args),
  debug: (...args) => console.debug('[LogContext]', ...args)
};

// Try to load state from localStorage for persistence across page reloads
try {
  const savedState = localStorage.getItem('log-monitor-state');
  if (savedState) {
    const parsed = JSON.parse(savedState);
    if (parsed && typeof parsed === 'object') {
      moduleState = {
        ...moduleState,
        ...parsed
      };
      LLogger.log('Loaded log state from localStorage');
    }
  }
} catch (e) {
  LLogger.warn('Failed to load log state from localStorage:', e);
}

// Initial state for the reducer
const initialState = {
  // Main data structure organized hierarchically: log_path → files → events
  // {
  //   [serverId]: {
  //     [log_path]: {
  //       status: 'active' | 'inactive',
  //       lastUpdated: timestamp,
  //       severity: 'Debug' | 'Info' | 'Warning' | 'Error' | 'Critical',
  //       health: 'good' | 'bad',
  //       today_files_count: number,
  //       host_name: string,
  //       host_ip: string,
  //       files: {
  //         [current_file]: {
  //           events: [...],
  //           severity: 'Debug' | 'Info' | 'Warning' | 'Error' | 'Critical',
  //           health: 'good' | 'bad',
  //           lastUpdated: timestamp
  //         }
  //       }
  //     }
  //   }
  // }
  logData: moduleState.logData || {},
  loading: false,
  error: null,
  lastUpdate: moduleState.lastUpdate || Date.now(),
  performanceMetrics: {
    totalEventsProcessed: 0,
    lastProcessTime: 0,
    averageProcessTime: 0
  }
};

// Action types for the reducer
const ActionTypes = {
  PROCESS_LOG_EVENT: 'PROCESS_LOG_EVENT',
  ARCHIVE_OLD_LOGS: 'ARCHIVE_OLD_LOGS',
  CLEAR_SERVER_LOGS: 'CLEAR_SERVER_LOGS',
  CLEAR_ALL_LOGS: 'CLEAR_ALL_LOGS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  UPDATE_PERFORMANCE_METRICS: 'UPDATE_PERFORMANCE_METRICS',
  SYNC_WITH_MODULE_STATE: 'SYNC_WITH_MODULE_STATE',
  FORCE_UPDATE: 'FORCE_UPDATE'
};

/**
 * Extract the highest severity level from an array of events
 * 
 * @param {Array} events - Array of log events
 * @returns {string} - The highest severity level found
 */
function getHighestSeverity(events) {
  if (!events || events.length === 0) return 'Info';
  
  let highest = 'Debug';
  let highestPriority = SEVERITY_LEVELS.Debug;
  
  events.forEach(event => {
    const level = event.level;
    if (SEVERITY_LEVELS[level] > highestPriority) {
      highest = level;
      highestPriority = SEVERITY_LEVELS[level];
    }
  });
  
  return highest;
}

/**
 * Determine health status based on severity
 * 
 * @param {string} severity - Severity level
 * @returns {string} - 'good' or 'bad' health indicator
 */
function calculateHealth(severity) {
  return (SEVERITY_LEVELS[severity] >= SEVERITY_LEVELS.Error) ? 'bad' : 'good';
}

/**
 * Reducer function to manage state updates
 * 
 * @param {Object} state - Current state
 * @param {Object} action - Action to perform
 * @returns {Object} - New state
 */
function logReducer(state, action) {
  switch (action.type) {
    case ActionTypes.PROCESS_LOG_EVENT: {
      const { serverId, logEvent, timestamp } = action.payload;
      const now = Date.now();
      
      // Make sure we have valid data
      if (!serverId || !logEvent || !logEvent.Log) {
        LLogger.warn('Invalid log event data', { serverId, logEvent });
        return state;
      }
      
      // Update module state to ensure persistence across component lifecycles
      moduleState.lastUpdate = now;
      
      // Extract log data
      const {
        log_path,
        current_file,
        events,
        name,
        tracked_events,
        today_files_count
      } = logEvent.Log;
      
      // Extract host info from the main event
      const host_name = logEvent.host_name || 'unknown';
      const host_ip = logEvent.host_ip || 'unknown';
      const status = logEvent.state || 'active';
      
      // Always create new references at every level
      const prevLogData = state.logData || {};
      const prevServer = prevLogData[serverId] || {};
      const prevLogPath = prevServer[log_path] || {};
      const prevFiles = prevLogPath.files || {};
      const prevFileEntry = prevFiles[current_file] || {
        events: [],
        severity: 'Info',
        health: 'good',
        lastUpdated: now
      };

      // Copy and update file entry
      const fileEntry = {
        ...prevFileEntry,
        lastUpdated: now
      };
      // Add new events
      if (events && events.length > 0) {
        // Check for duplicates by timestamp+message
        const prevEventKeys = new Set(prevFileEntry.events.map(e => `${e.timestamp}_${e.message}`));
        const dedupedNewEvents = events.filter(e => {
          const key = `${e.timestamp}_${e.message}`;
          if (prevEventKeys.has(key)) {
            LLogger.warn(`[PROCESS_LOG_EVENT] Duplicate event detected for file '${current_file}':`, e);
            return false;
          }
          return true;
        });

        // Log deduplication results
        if (dedupedNewEvents.length !== events.length) {
          LLogger.warn(`[PROCESS_LOG_EVENT] Deduplicated ${events.length - dedupedNewEvents.length} events for file '${current_file}'`);
        }

        // Merge and sort events by timestamp descending
        const mergedEvents = [...prevFileEntry.events, ...dedupedNewEvents];
        const validEvents = mergedEvents.filter(e => {
          if (!e.timestamp || isNaN(new Date(e.timestamp).getTime())) {
            LLogger.warn(`[PROCESS_LOG_EVENT] Event missing or malformed timestamp in file '${current_file}':`, e);
            return false;
          }
          return true;
        });
        validEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        fileEntry.events = validEvents;
        fileEntry.severity = getHighestSeverity(fileEntry.events);
        fileEntry.health = calculateHealth(fileEntry.severity);
      }

      // Copy and update files
      const files = {
        ...prevFiles,
        [current_file]: fileEntry
      };

      // Copy and update log_path
      const logPathEntry = {
        ...prevLogPath,
        status,
        lastUpdated: now,
        today_files_count: today_files_count || prevLogPath.today_files_count || 0,
        host_name,
        host_ip,
        name: name || prevLogPath.name || 'unknown',
        tracked_events: tracked_events || prevLogPath.tracked_events || [],
        files
      };
      // Update severity and health based on all files
      const allFileSeveritiesProcess = Object.values(files).map(file => file.severity);
      logPathEntry.severity = getHighestSeverity(allFileSeveritiesProcess.map(severity => ({ level: severity })));
      logPathEntry.health = calculateHealth(logPathEntry.severity); // in PROCESS_LOG_EVENT

      // Copy and update server
      const server = {
        ...prevServer,
        [log_path]: logPathEntry
      };
      // Copy and update logData
      const logData = {
        ...prevLogData,
        [serverId]: server
      };
      return {
        ...state,
        logData,
        lastUpdate: now
      };
    }
    
    case ActionTypes.ARCHIVE_OLD_LOGS: {
      // Create new state to avoid mutation
      const newState = { ...state };
      const now = Date.now();
      
      // Process each server
      Object.keys(newState.logData).forEach(serverId => {
        const serverLogs = newState.logData[serverId];
        
        // Process each log_path
        Object.keys(serverLogs).forEach(logPath => {
          const logPathEntry = serverLogs[logPath];
          
          // Process each file
          Object.keys(logPathEntry.files).forEach(fileName => {
            const fileEntry = logPathEntry.files[fileName];
            
            // Filter events based on severity and age
            fileEntry.events = fileEntry.events.filter(event => {
              // Keep all events for now if we're not archiving
              if (!event.timestamp) return true;
              
              const eventTime = new Date(event.timestamp).getTime();
              const age = now - eventTime;
              
              // Keep critical/error logs longer
              if (event.level === 'Critical' || event.level === 'Error') {
                return age < RETENTION.CRITICAL_ERROR;
              }
              
              // Keep other logs for shorter time
              return age < RETENTION.OTHER;
            });
            
            // Update severity and health
            if (fileEntry.events.length > 0) {
              fileEntry.severity = getHighestSeverity(fileEntry.events);
              fileEntry.health = calculateHealth(fileEntry.severity);
            }
          });
          
          // Update log_path severity and health
          const allFileSeveritiesArchive = Object.values(logPathEntry.files).map(file => file.severity);
          logPathEntry.severity = getHighestSeverity(allFileSeveritiesArchive.map(severity => ({ level: severity })));
          logPathEntry.health = calculateHealth(logPathEntry.severity);
        });
      });
      
      return newState;
    }
    
    case ActionTypes.CLEAR_SERVER_LOGS: {
      const { serverId } = action.payload;
      
      // Create new state to avoid mutation
      const newState = { ...state };
      
      // Remove server logs
      if (newState.logData[serverId]) {
        delete newState.logData[serverId];
      }
      
      return newState;
    }
    
    case ActionTypes.CLEAR_ALL_LOGS: {
      // Reset log data
      // Update module state
      moduleState.logData = {};
      moduleState.lastUpdate = Date.now();
      
      // Clear localStorage
      try {
        localStorage.removeItem('log-monitor-state');
      } catch (e) {
        LLogger.warn('Failed to clear log state from localStorage:', e);
      }
      
      return {
        ...state,
        logData: {},
        lastUpdate: Date.now()
      };
    }
    
    case ActionTypes.SET_LOADING: {
      return {
        ...state,
        loading: action.payload
      };
    }
    
    case ActionTypes.SET_ERROR: {
      return {
        ...state,
        error: action.payload
      };
    }
    
    case ActionTypes.UPDATE_PERFORMANCE_METRICS: {
      return {
        ...state,
        performanceMetrics: {
          ...state.performanceMetrics,
          ...action.payload
        }
      };
    }
    
    default:
      return state;
  }
}

/**
 * Provider component for Log data persistence
 * 
 * This allows log data to persist when switching tabs or panels, and ensures
 * that all log events are processed in the background regardless of which component is visible.
 * 
 * @component
 */
export function LogProvider({ children }) {
  const [state, dispatch] = useReducer(logReducer, initialState);
  const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
  
  // Visibility detection for better tab status tracking
  const isVisible = useRef(true);
  
  // Health check system to detect and recover from stalled updates
  const lastHealthCheck = useRef(Date.now());
  const healthCheckInterval = useRef(null);
        
  // Ref to store timestamp references for deduplication
  const extractLogData = useCallback((eventData) => {
    if (!eventData) {
      LLogger.warn('Empty event data');
      return null;
    }
        
    // Check for direct Log property
    if (eventData.Log) {
      return eventData;
    }
        
    // Check for Events wrapper (legacy format)
    if (eventData.Events && eventData.Events.Log) {
      return {
        ...eventData,
        Log: eventData.Events.Log
      };
    }
        
    // Check for event_data.Log
    if (eventData.event_data && eventData.event_data.Log) {
      return {
        ...eventData,
        Log: eventData.event_data.Log
      };
    }
        
    // Check for data.Log
    if (eventData.data && eventData.data.Log) {
      return {
        ...eventData,
        Log: eventData.data.Log
      };
    }
        
    LLogger.warn('Failed to extract log data from event', eventData);
    return null;
  }, []);
  
  /**
   * Process a log event and update the state
   * 
   * @param {string} serverId - The ID of the server that sent the event
   * @param {Object} eventData - The raw event data
   * @param {string} timestamp - ISO timestamp when the event was received
   * @returns {boolean} - Whether the event was processed successfully
   */
  const processLogEvent = useCallback((serverId, eventData, timestamp) => {
    if (!serverId || !eventData) {
      LLogger.warn('Invalid log event parameters', { serverId, eventData });
      return false;
    }
    
    // Normalize server ID
    const normalizedServerId = serverId.toLowerCase();
    
    // Extract log data
    const logEvent = extractLogData(eventData);
    if (!logEvent || !logEvent.Log) {
      LLogger.warn('Failed to extract valid log data from event', eventData);
      return false;
    }
    
    // Get event timestamp for ordering
    const eventTime = eventData.event_time || timestamp;
    
    // Process all incoming log events without deduplication
    // The deduplication is handled upstream in chronosight
    
    try {
      // Measure processing time
      const startTime = performance.now();
      
      // Dispatch the log event for processing
      dispatch({
        type: ActionTypes.PROCESS_LOG_EVENT,
        payload: {
          serverId: normalizedServerId,
          logEvent,
          timestamp: eventTime
        }
      });
      
      // Update performance metrics
      const endTime = performance.now();
      const processTime = endTime - startTime;
      
      const { totalEventsProcessed, averageProcessTime } = state.performanceMetrics;
      const newTotal = totalEventsProcessed + 1;
      const newAverage = (averageProcessTime * totalEventsProcessed + processTime) / newTotal;
      
      dispatch({
        type: ActionTypes.UPDATE_PERFORMANCE_METRICS,
        payload: {
          totalEventsProcessed: newTotal,
          lastProcessTime: processTime,
          averageProcessTime: newAverage
        }
      });
      
      return true;
    } catch (error) {
      LLogger.error('Error processing log event', error, { serverId, eventData });
      dispatch({
        type: ActionTypes.SET_ERROR,
        payload: error.message
      });
      return false;
    }
  }, [state.performanceMetrics, extractLogData]);
  
  /**
   * Get all log data for a specific server
   * 
   * @param {string} serverId - The ID of the server
   * @returns {Object|null} - Log data for the server or null if not found
   */
  const getServerLogs = useCallback((serverId) => {
    if (!serverId) return null;
    
    const normalizedServerId = serverId.toLowerCase();
    return state.logData[normalizedServerId] || null;
  }, [state.logData]);
  
  /**
   * Clear all logs for a specific server
   * 
   * @param {string} serverId - The ID of the server
   */
  const clearServerLogs = useCallback((serverId) => {
    if (!serverId) {
      LLogger.warn('No serverId provided to clearServerLogs');
      return;
    }
    
    const normalizedServerId = serverId.toLowerCase();
    
    dispatch({
      type: ActionTypes.CLEAR_SERVER_LOGS,
      payload: { serverId: normalizedServerId }
    });
    
    // No timestamp references to clear since we removed deduplication
    
    LLogger.log(`Cleared logs for server ${normalizedServerId}`);
  }, []);
  
  /**
   * Clear all logs for all servers
   */
  const clearAllLogs = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ALL_LOGS });
    LLogger.log('Cleared all logs');
  }, []);
  
  /**
   * Get performance metrics for monitoring and debugging
   * 
   * @returns {Object} - Performance metrics
   */
  const getPerformanceMetrics = useCallback(() => {
    const { logData, performanceMetrics } = state;
    
    // Calculate additional metrics
    const serverCount = Object.keys(logData).length;
    let pathCount = 0;
    let fileCount = 0;
    let eventCount = 0;
    
    // Count paths, files, and events
    Object.values(logData).forEach(serverLogs => {
      const paths = Object.keys(serverLogs);
      pathCount += paths.length;
      
      paths.forEach(path => {
        const files = Object.keys(serverLogs[path].files);
        fileCount += files.length;
        
        files.forEach(file => {
          eventCount += serverLogs[path].files[file].events.length;
        });
      });
    });
    
    return {
      ...performanceMetrics,
      serverCount,
      pathCount,
      fileCount,
      eventCount
    };
  }, [state]);
  
  /**
   * Run the archive job to mark old logs as archived
   */
  const archiveOldLogs = useCallback(() => {
    dispatch({ type: ActionTypes.ARCHIVE_OLD_LOGS });
  }, []);
  
  // Set up auto archiving every minute
  useEffect(() => {
    const archiveInterval = setInterval(() => {
      archiveOldLogs();
    }, 60000); // Every minute
    
    return () => clearInterval(archiveInterval);
  }, [archiveOldLogs]);
  
  // Force update mechanism to ensure UI reflects latest data
  const forceUpdate = useCallback(() => {
    setForceUpdateCounter(prev => prev + 1);
    dispatch({ type: ActionTypes.FORCE_UPDATE });
  }, []);
  
  // Sync with module state
  const syncWithModuleState = useCallback(() => {
    dispatch({ type: ActionTypes.SYNC_WITH_MODULE_STATE });
  }, []);
  
  // Health check function to detect stalled updates
  const performHealthCheck = useCallback(() => {
    const now = Date.now();
    const timeSinceLastCheck = now - lastHealthCheck.current;
    
    // If it's been too long since the last health check (over 10 seconds)
    if (timeSinceLastCheck > 10000) {
      LLogger.warn(`Health check detected potential stall: ${timeSinceLastCheck}ms since last check`);
      syncWithModuleState();
      forceUpdate();
    }
    
    lastHealthCheck.current = now;
  }, [syncWithModuleState, forceUpdate]);
  
  // Setup health check interval
  useEffect(() => {
    healthCheckInterval.current = setInterval(performHealthCheck, 5000);
    return () => clearInterval(healthCheckInterval.current);
  }, [performHealthCheck]);
  
  // Listen for visibility change events
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasVisible = isVisible.current;
      isVisible.current = document.visibilityState === 'visible';
      
      // If becoming visible after being hidden, sync state
      if (isVisible.current && !wasVisible) {
        LLogger.log('Tab became visible, syncing log state');
        syncWithModuleState();
        forceUpdate();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [syncWithModuleState, forceUpdate]);
  
  // Listen for broadcast channel messages
  useEffect(() => {
    if (broadcastChannel) {
      const handleBroadcastMessage = (event) => {
        if (event.data && event.data.type === 'LOG_UPDATE') {
          // Only sync if we're visible to avoid unnecessary work in background tabs
          if (isVisible.current) {
            syncWithModuleState();
          }
        }
      };
      
      broadcastChannel.addEventListener('message', handleBroadcastMessage);
      return () => broadcastChannel.removeEventListener('message', handleBroadcastMessage);
    }
  }, [syncWithModuleState]);
  
  // Listen for log-update events from WebSocketContext
  useEffect(() => {
    function handleLogUpdate(event) {
      const { serverId, data, timestamp } = event.detail || {};
      if (serverId && data) {
        processLogEvent(serverId, data, timestamp || new Date().toISOString());
      }
    }
    window.addEventListener('log-update', handleLogUpdate);
    return () => window.removeEventListener('log-update', handleLogUpdate);
  }, [processLogEvent]);
  
  // Listen for log-data-updated events for real-time updates
  useEffect(() => {
    const handleLogDataUpdated = () => {
      // This is intentionally left minimal to avoid excessive re-renders
      // The actual data is already in the state via the reducer
      if (isVisible.current) {
        forceUpdate();
      }
    };
    
    window.addEventListener('log-data-updated', handleLogDataUpdated);
    return () => window.removeEventListener('log-data-updated', handleLogDataUpdated);
  }, [forceUpdate]);
  
  // Provide context value
  const contextValue = {
    ...state,
    processLogEvent,
    getServerLogs,
    clearServerLogs,
    clearAllLogs,
    getPerformanceMetrics,
    forceUpdate,
    syncWithModuleState,
    forceUpdateCounter // Include this to help components know when to re-render
  };
  
  return (
    <LogContext.Provider value={contextValue}>
      {children}
    </LogContext.Provider>
  );
}

/**
 * Custom hook to use the Log context
 * 
 * @returns {Object} - Log context value
 */
export function useLogContext() {
  const context = useContext(LogContext);
  if (context === undefined) {
    throw new Error('useLogContext must be used within a LogProvider');
  }
  return context;
}
